import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import EmployeePicker from "../../components/ui/EmployeePicker";
import {
  useApproverList,
  useEmployeeApprovers,
  useSetEmployeeApprovers,
  useSetManyApprovers,
} from "../../api/queries/workflowQueries";
import EffectiveApprovers from "../admin/approval-rules/EffectiveApprovers";
import ApprovalSetupTabs from "../admin/approval-rules/ApprovalSetupTabs";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { employeeName } from "../../utils/text";
import "../attendance/attendance.css";
import "../admin/admin.css";
import "./approvers.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];
const MAX_LEVELS = 5;
// "" = the chain for every request type; the others override it for one type
const CHAIN_TYPES = [
  { value: "", label: "All types" },
  { value: "DTR", label: "DTR" },
  { value: "OVERTIME", label: "Overtime" },
  { value: "LEAVE", label: "Leave" },
  { value: "VALIDATION", label: "Check-in validation" },
];
const toPicked = (list = []) =>
  list.map((a) => ({
    id: a.employeeId,
    employeeId: a.employeeNumber,
    firstName: a.name,
    lastName: "",
  }));
const sameIds = (a = [], b = []) =>
  a.length === b.length && a.every((x, i) => x.id === b[i].id);

/** Level 1 → Level 2 … chips. */
export const ApproverChain = ({ approvers = [] }) =>
  approvers.length === 0 ? (
    <span className="text-slate-400 fs-075">None: approval rules apply</span>
  ) : (
    <div className="approver-chain">
      {approvers.map((a, i) => (
        <React.Fragment key={a.level}>
          {i > 0 && <i className="bi bi-arrow-right text-slate-400"></i>}
          <span className="approver-chip" title={a.employeeNumber}>
            <span className="approver-level">L{a.level}</span>
            {a.name}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

/**
 * Ordered approver picker (level 1, 2, … max 5). `targets` are the employees
 * being edited (one → PUT one; many → bulk PUT with the same chain).
 */
const ChainDialog = ({ targets, initial, onClose }) => {
  const user = useCurrentUser();
  const isBulk = targets.length > 1;
  const targetIds = targets.map((t) => t.employeeId);
  // One employee: every chain (all types + per-type overrides) from the server
  const detail = useEmployeeApprovers(isBulk ? null : targetIds[0]);
  const [type, setType] = useState("");
  // Saved chains per type (from the server) and the edited copies
  const [saved, setSaved] = useState(() => ({ "": toPicked(initial) }));
  const [drafts, setDrafts] = useState(() => ({ "": toPicked(initial) }));
  useEffect(() => {
    const chains = detail.data?.chains;
    if (!chains) return;
    const map = {};
    chains.forEach((c) => (map[c.requestType || ""] = toPicked(c.approvers)));
    if (!map[""]) map[""] = toPicked(detail.data.approvers);
    setSaved(map);
    setDrafts(map);
  }, [detail.data]);
  const chain = drafts[type] ?? [];
  const setChain = (fn) =>
    setDrafts((d) => ({
      ...d,
      [type]: typeof fn === "function" ? fn(d[type] ?? []) : fn,
    }));
  const changedTypes = CHAIN_TYPES.map((t) => t.value).filter(
    (t) => drafts[t] !== undefined && !sameIds(drafts[t], saved[t] ?? []),
  );
  const hasOverride = (t) => (saved[t] ?? []).length > 0;
  const setOne = useSetEmployeeApprovers();
  const setMany = useSetManyApprovers();
  const busy = setOne.isPending || setMany.isPending;

  const move = (i, delta) =>
    setChain((list) => {
      const next = [...list];
      const [x] = next.splice(i, 1);
      next.splice(i + delta, 0, x);
      return next;
    });

  // Saves every type that changed (all-types chain and/or overrides)
  const save = async () => {
    try {
      for (const t of changedTypes) {
        const ids = (drafts[t] ?? []).map((c) => c.id);
        const requestType = t || undefined;
        if (isBulk)
          await setMany.mutateAsync({
            employeeIds: targetIds,
            approverEmployeeIds: ids,
            requestType,
          });
        else
          await setOne.mutateAsync({
            employeeId: targetIds[0],
            approverEmployeeIds: ids,
            requestType,
          });
      }
      toast.success(
        isBulk
          ? `Approvers set for ${targets.length} employees.`
          : "Approvers saved.",
      );
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not save the approvers."));
    }
  };

  return (
    <Dialog
      title={
        isBulk
          ? `Approvers for ${targets.length} employees`
          : `Approvers of ${targets[0].name}`
      }
      subtitle={
        isBulk
          ? "The same chain replaces each selected employee's approvers."
          : targets[0].employeeNumber
      }
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={save}
            disabled={busy || changedTypes.length === 0}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      <div
        className="approval-subtabs mb-2"
        role="tablist"
        aria-label="Request type"
      >
        {CHAIN_TYPES.map((t) => (
          <button
            key={t.value || "all"}
            type="button"
            role="tab"
            aria-selected={type === t.value}
            className={`approval-subtab ${type === t.value ? "active" : ""}`}
            onClick={() => setType(t.value)}
          >
            {t.label}
            {t.value && hasOverride(t.value) && (
              <i
                className="bi bi-dot text-primary"
                title="Has its own approvers"
              ></i>
            )}
            {changedTypes.includes(t.value) && " *"}
          </button>
        ))}
      </div>
      <p className="fs-080 text-slate-600">
        {type
          ? `Approvers for ${CHAIN_TYPES.find((t) => t.value === type).label} only. They replace the "All types" chain for this request. Leave it empty to use the "All types" chain.`
          : "Approvals go level by level (DTR, overtime, leave, evaluations). This chain overrides the company's approval rules. Leave it empty to use the rules (or the default: branch manager, then HR)."}
      </p>
      {!isBulk && detail.isLoading && (
        <p className="fs-075 text-slate-500">Loading the current chains…</p>
      )}
      <ol className="approver-edit-list">
        {chain.map((c, i) => (
          <li key={c.id}>
            <span className="approver-level">L{i + 1}</span>
            <span className="flex-grow-1">
              <strong>{employeeName(c)}</strong>{" "}
              <span className="text-slate-500 fs-075">{c.employeeId}</span>
            </span>
            <div className="row-tools">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move up"
              >
                <i className="bi bi-arrow-up"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => move(i, 1)}
                disabled={i === chain.length - 1}
                aria-label="Move down"
              >
                <i className="bi bi-arrow-down"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light text-danger"
                onClick={() => setChain((l) => l.filter((x) => x.id !== c.id))}
                aria-label="Remove"
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </li>
        ))}
        {chain.length === 0 && (
          <li className="text-slate-500 fs-080">
            {type
              ? "No override: uses the All types chain."
              : "No approvers: default chain."}
          </li>
        )}
      </ol>
      {chain.length < MAX_LEVELS ? (
        <>
          <label htmlFor="approver-add" className="profile-label mt-2">
            Add level {chain.length + 1}
          </label>
          <EmployeePicker
            id="approver-add"
            companyId={user.companyId}
            value={null}
            onChange={(emp) => emp && setChain((l) => [...l, emp])}
            excludeId={[
              ...chain.map((c) => c.id),
              ...(isBulk ? [] : targetIds),
            ]}
            placeholder="Search approver"
          />
        </>
      ) : (
        <p className="fs-075 text-slate-500 mt-2 mb-0">
          Maximum of {MAX_LEVELS} levels.
        </p>
      )}
    </Dialog>
  );
};

/** Effective approvers per request type for one employee (own chain, rule or default). */
const RoutingDialog = ({ row, onClose }) => {
  const query = useEmployeeApprovers(row.employeeId);
  return (
    <Dialog
      title={`Who approves ${row.name}`}
      subtitle={row.employeeNumber}
      onClose={onClose}
    >
      {query.isLoading && (
        <div className="text-center py-3">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(query.error, "Could not load the routing.")}
        </div>
      )}
      {query.data && <EffectiveApprovers byType={query.data.byType || []} />}
      <p className="fs-075 text-slate-500 mt-3 mb-0">
        Company rules per request are set in{" "}
        <Link to="/admin/approval-rules">Approval Setup</Link>. HR can always
        approve.
      </p>
    </Dialog>
  );
};

/** Admins/HR: who approves each employee's DTR, overtime, leave and evaluations. */
const ApproversPage = () => {
  // HR-level (employees with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("employees");
  const [branchId, setBranchId] = useState("");
  const [params] = useSearchParams(); // ?search= from the employee profile link
  const [searchText, setSearchText] = useState(
    () => params.get("search") || "",
  );
  const [search, setSearch] = useState(() => params.get("search") || "");
  const [missingOnly, setMissingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selected, setSelected] = useState(() => new Map()); // employeeId → row
  const [editing, setEditing] = useState(null); // { targets, initial }
  const [routing, setRouting] = useState(null); // row
  const canModify = useCanModify("employees", { company: true }); // view-only: see chains and routing, no edits

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useApproverList({ branchId, search, missingOnly, page, limit });
  const rows = query.data?.data || EMPTY;
  const allOnPage =
    rows.length > 0 && rows.every((r) => selected.has(r.employeeId));

  const toggle = (r) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.has(r.employeeId)
        ? next.delete(r.employeeId)
        : next.set(r.employeeId, r);
      return next;
    });

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="att-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can set
            approvers.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4">
          <h1 className="fw-bold text-dark m-0 page-title">Approval Setup</h1>
          <p className="text-muted small mb-0">
            Give an employee their own approvers (level 1 first) when the
            company rule doesn't fit them.
          </p>
        </div>

        <ApprovalSetupTabs />

        <div className="att-filters mb-3">
          <div className="position-relative att-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Name or employee no."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search"
            />
          </div>
          <div className="att-filter-branch">
            <BranchSelect
              value={branchId}
              onChange={(v) => {
                setBranchId(v || "");
                setPage(1);
              }}
              id="approvers-branch"
            />
          </div>
          <div className="form-check form-switch m-0">
            <input
              id="approvers-missing"
              type="checkbox"
              className="form-check-input"
              checked={missingOnly}
              onChange={(e) => {
                setMissingOnly(e.target.checked);
                setPage(1);
              }}
            />
            <label
              htmlFor="approvers-missing"
              className="form-check-label fs-080"
            >
              No approver only
            </label>
          </div>
        </div>

        {selected.size > 0 && canModify && (
          <div className="att-bulk-bar">
            <strong className="fs-085">{selected.size} selected</strong>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() =>
                  setEditing({ targets: [...selected.values()], initial: [] })
                }
              >
                <i className="bi bi-diagram-3 me-1"></i>Set the same approvers
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link text-slate-600"
                onClick={() => setSelected(new Map())}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load approvers.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 att-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3 att-col-expand">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={allOnPage}
                          onChange={() =>
                            setSelected((prev) => {
                              const next = new Map(prev);
                              rows.forEach((r) =>
                                allOnPage
                                  ? next.delete(r.employeeId)
                                  : next.set(r.employeeId, r),
                              );
                              return next;
                            })
                          }
                          aria-label="Select all on this page"
                        />
                      </th>
                      <th className="py-3">Employee</th>
                      <th className="py-3">Branch</th>
                      <th className="py-3">Approvers</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {rows.map((r) => (
                      <tr
                        key={r.employeeId}
                        className={
                          selected.has(r.employeeId) ? "att-row-selected" : ""
                        }
                      >
                        <td className="ps-4">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.has(r.employeeId)}
                            onChange={() => toggle(r)}
                            aria-label={`Select ${r.name}`}
                          />
                        </td>
                        <td>
                          <div className="emp-name-text">{r.name}</div>
                          <div className="emp-sub-text">{r.employeeNumber}</div>
                        </td>
                        <td>{r.branchName || "—"}</td>
                        <td>
                          <ApproverChain approvers={r.approvers} />
                        </td>
                        <td className="pe-4 text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn me-1"
                            onClick={() => setRouting(r)}
                            title="Who approves each request type"
                          >
                            <i className="bi bi-signpost-split"></i> Routing
                          </button>
                          {canModify && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light border att-action-btn"
                              onClick={() =>
                                setEditing({
                                  targets: [r],
                                  initial: r.approvers,
                                })
                              }
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!query.isLoading && rows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="approval-empty">
                          <i className="bi bi-diagram-3"></i>
                          {missingOnly
                            ? "Everyone has approvers."
                            : "No employees found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={query.data?.total || 0}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            </>
          )}
        </div>
      </div>

      {routing && (
        <RoutingDialog row={routing} onClose={() => setRouting(null)} />
      )}
      {editing && (
        <ChainDialog
          targets={editing.targets}
          initial={editing.initial}
          onClose={() => {
            if (editing.targets.length > 1) setSelected(new Map());
            setEditing(null);
          }}
        />
      )}
    </Layout>
  );
};

export default ApproversPage;
